import { Autocomplete, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllFormTemplate } from '../../../services/react-query/form_templates';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { FormTemplate, FormTemplateLink } from '../../../types/FormTemplate';
import { PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { Nullable } from '../../../types/utils';
import PreferencePicker from '../../preferences/picker/PreferencePicker';

interface IAddFormTemplateModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onAdd: (linkingData: FormTemplateLink) => void;
  formTemplateLink?: FormTemplateLink | null;
}

type ModalState = Nullable<Omit<FormTemplateLink, 'uuid'>>;

const BLANK_STATE: ModalState = {
  form_template_uuid: null,
  form_template_name: null,
  event_template_form_template_uuid: null,
  open_offset: null,
  close_offset: null,
  roles_uuids: [],
  roles_values: [],
};

export default function AddFormTemplateModal({ isOpen, onCancel, onAdd, formTemplateLink }: IAddFormTemplateModalProps) {
  const [modalState, setModalState] = useState<ModalState>(BLANK_STATE);

  const { data: preferences, isFetching: isPrefsFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE] } },
  });

  const { data, isLoading } = useGetAllFormTemplate({ params: { per_page: 0 } });
  const templates: FormTemplate[] = data?.data ?? [];

  const selectedRoleObjects = useMemo(() => _.filter(preferences, (p) => _.includes(modalState.roles_uuids, p.uuid)), [preferences, modalState.roles_uuids]);

  // everything needs values
  const isValid = !_.some(_.flatten(
    [
      _.map([modalState.open_offset, modalState.close_offset, modalState.form_template_uuid], _.isNil),
      _.isEmpty(modalState.roles_uuids),
      (modalState.open_offset ?? 0) > (modalState.close_offset ?? 0),
    ]
  ));

  useEffect(() => {
    if (formTemplateLink) {
      const selectedPrefs = _.filter(preferences, (p) => _.includes(formTemplateLink.roles_uuids, p.uuid));
      setModalState({
        form_template_uuid: formTemplateLink.form_template_uuid ?? null,
        form_template_name: formTemplateLink.form_template_name ?? null,
        event_template_form_template_uuid: formTemplateLink.event_template_form_template_uuid ?? null,
        open_offset: formTemplateLink.open_offset ?? null,
        close_offset: formTemplateLink.close_offset ?? null,
        roles_uuids: _.map(selectedPrefs, 'uuid'),
        roles_values: _.map(selectedPrefs, 'value'),
      });
    } else {
      setModalState(BLANK_STATE);
    }
  }, [isOpen, formTemplateLink, preferences]);

  const handleAdd = () => {
    if (!isValid) return;

    onAdd({
      uuid: formTemplateLink?.uuid || crypto.randomUUID(),
      ...modalState,
    } as FormTemplateLink);
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        Please choose which template to add
      </Typography>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Autocomplete<FormTemplate>
          loading={isLoading}
          options={templates}
          onChange={(_, template) =>
            setModalState((prev) => ({
              ...prev,
              form_template_uuid: template?.uuid ?? null,
              form_template_name: template?.name ?? null,
            }))
          }
          getOptionLabel={(template) => template?.name || ''}
          value={_.find(templates, (t) => t.uuid === modalState.form_template_uuid) || null}
          renderInput={(params) => <TextField {...params} autoFocus label="Form Templates" />}
        />

        <TextField
          label="Open Offset (Days)"
          type="number"
          value={modalState.open_offset ?? ''}
          onChange={(e) => setModalState((prev) => ({ ...prev, open_offset: e.target.value === '' ? null : Number(e.target.value) }))}
        />

        <TextField
          label="Close Offset (Days)"
          type="number"
          value={modalState.close_offset ?? ''}
          onChange={(e) => setModalState((prev) => ({ ...prev, close_offset: e.target.value === '' ? null : Number(e.target.value) }))}
        />

        <PreferencePicker
          label="Contact Roles"
          multiple
          options={preferences ?? []}
          loading={isPrefsFetching}
          value={selectedRoleObjects}
          onValueChange={(vals) => setModalState((prev) => (
            {
              ...prev,
              roles_uuids: _.map(vals, 'uuid'),
              roles_values: _.map(vals, 'value'),
            }
          ))}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={!isValid} onClick={handleAdd}>
          {formTemplateLink ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
